'use client'

import { useTranslations } from 'next-intl'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { VariantFormLabel } from '@/components/common/form/variant-form-label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'

import { FEEDBACK_TYPE_OPTIONS } from '@/constants/feedback'
import { useZodResolver } from '@/hooks/use-zod-resolver'
import {
  FEEDBACK_ATTACHMENT_ALLOWED_FORMATS,
  FEEDBACK_ATTACHMENT_MAX_FILE_SIZE_MB,
  FEEDBACK_ATTACHMENT_MAX_TOTAL_SIZE_MB,
} from '@/lib/feedback-attachments'
import { FeedbackSchema } from '@/schema/feedback-schema'
import { createFeedbackService } from '@/services/feedback-service'

import DashboardLoadingIcon from '../icon/dashboard-loading-icon'

import { FeedbackAttachmentUploader } from './feedback-attachment-uploader'

interface Props {
  isOpen: boolean
  onClose: Dispatch<SetStateAction<boolean>>
}

const FeedbackDialog = ({ isOpen, onClose }: Props) => {
  // @TODO: Refactor similar to forgot password
  const t = useTranslations('Feedback')
  // const tFormCurrent = useTranslations('Appearance.forms.appearance_page_form')
  const tFormFields = useTranslations('Feedback.forms.feedback_dialog_form.fields')
  const resolver = useZodResolver({ schema: FeedbackSchema, tFormFields })

  const { toast } = useToast()
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false)

  // 1. Define your form.
  const feedbackForm = useForm<typeof FeedbackSchema._type>({
    resolver,
    defaultValues: {
      type: FEEDBACK_TYPE_OPTIONS[2]?.value,
      message: '',
      files: [],
    },
  })

  const message = useWatch({ control: feedbackForm.control, name: 'message' })
  const canSendFeedback = !!message?.trim()
  // Read during render so react-hook-form tracks them; isDirty compares against defaultValues
  const { isDirty, isSubmitting } = feedbackForm.formState

  // 2. Define a submit handler.
  const onSubmit = async (values: typeof FeedbackSchema._type) => {
    const { message, type, files } = values
    const { data: createFeedbackResultData, ok } = await createFeedbackService({
      type,
      message,
      files,
    })

    if (!ok) {
      toast({
        variant: 'error',
        description: createFeedbackResultData?.message,
        duration: 3000,
      })
    } else {
      toast({
        variant: 'success',
        title: t('submittedSuccess.title'),
        description: t('submittedSuccess.description'),
        duration: 3000,
      })
      closeAndReset()
    }
  }

  const handleSubmit = async () => {
    await feedbackForm.handleSubmit(onSubmit)()
  }

  const closeAndReset = () => {
    setIsDiscardConfirmOpen(false)
    onClose(false)
    feedbackForm.reset()
  }

  // Every close path (X, Cancel, Esc, outside click) lands here. Anything typed or attached is
  // confirmed before it is thrown away; an untouched form closes straight away.
  const requestClose = () => {
    if (isSubmitting) return
    if (isDirty) {
      setIsDiscardConfirmOpen(true)
      return
    }
    closeAndReset()
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) requestClose()
        }}
      >
        {/* Capped to the viewport; header and footer stay put while the fields scroll */}
        <DialogContent className="bottom-0 top-auto flex max-h-[90dvh] max-w-full translate-y-0 flex-col gap-6 rounded-b-none rounded-t-[16px] sm:bottom-auto sm:top-1/2 sm:max-h-[calc(100dvh-2rem)] sm:max-w-[550px] sm:-translate-y-1/2 sm:rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-left text-lg font-semibold text-base-card-foreground">
              {t('title')}
            </DialogTitle>
            <DialogDescription className="text-left text-sm font-normal text-muted-foreground">
              {t('description')}
            </DialogDescription>
          </DialogHeader>
          <Form {...feedbackForm}>
            {/* Negative margin + matching padding puts the scrollbar at the dialog edge and keeps focus rings unclipped */}
            <div className="-mx-6 -my-1 min-h-0 flex-1 space-y-8 overflow-y-auto px-6 py-1">
              <FormField
                control={feedbackForm.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <VariantFormLabel>{t('type')}</VariantFormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value)
                      }}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              FEEDBACK_TYPE_OPTIONS.find((typeOption) => typeOption.value === field.value)?.label
                            }
                          ></SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {FEEDBACK_TYPE_OPTIONS.map((typeOption) => (
                          <SelectItem key={typeOption.label} value={typeOption.value}>
                            {t(`typeOptions.${typeOption.label}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={feedbackForm.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <VariantFormLabel>{t('message')}</VariantFormLabel>
                    <FormControl>
                      <Textarea {...field} rows={5} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={feedbackForm.control}
                name="files"
                render={({ field }) => (
                  <FormItem>
                    <VariantFormLabel>{t('attach')}</VariantFormLabel>
                    <p className="text-xs font-normal text-muted-foreground">
                      {t('attachSupport', {
                        formats: FEEDBACK_ATTACHMENT_ALLOWED_FORMATS.join(', '),
                        size: FEEDBACK_ATTACHMENT_MAX_FILE_SIZE_MB,
                        total: FEEDBACK_ATTACHMENT_MAX_TOTAL_SIZE_MB,
                      })}
                    </p>
                    <FormControl>
                      <FeedbackAttachmentUploader
                        files={field.value ?? []}
                        onFilesChange={field.onChange}
                        loading={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Form>
          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button type="button" variant="outline" onClick={requestClose} disabled={isSubmitting}>
              {t('cancel')}
            </Button>

            <Button type="button" disabled={isSubmitting || !canSendFeedback} onClick={handleSubmit}>
              {t('submit')}
              {isSubmitting && <DashboardLoadingIcon className="ml-2 text-base-muted" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDiscardConfirmOpen} onOpenChange={setIsDiscardConfirmOpen}>
        <AlertDialogContent className="max-w-[350px] rounded-lg md:max-w-[450px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('discard.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('discard.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <div className="flex w-full flex-col justify-center gap-4 md:flex-row md:justify-end">
              <AlertDialogCancel variant="outline">{t('discard.keepEditing')}</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={closeAndReset}>
                {t('discard.confirm')}
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default FeedbackDialog
